import { mergePageFieldMaps, overlayAuthoritativeFieldKeys } from '@/common/utils/mergePageFieldMaps'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import {
  isUniDataTableFieldShape,
} from '@/common/utils/omitEmptyUniDataTableFieldsFromReloadPayload'
import { sanitizeClientDataFieldMap } from '@/common/utils/resolveClientDataActualFields'
import { stripServerEventOverlayFields } from '@/common/utils/stripServerEventOverlayFields'

const DOCUMENT_IDENTITY_KEYS = [
  '_id',
  '_typeId',
  '_versionId',
  '_typeVersionId',
  '_typeVersion',
] as const

export function resolveReloadPanelScopedPageField(
  key: string,
  pageFieldsSnapshot: Record<string, unknown>,
  enFields: Record<string, unknown>,
): unknown | undefined {
  const fromPage = pageFieldsSnapshot[key]
  const fromEn = enFields[key]
  const pageIsTable = isUniDataTableFieldShape(fromPage)
  const enIsTable = isUniDataTableFieldShape(fromEn)
  if (pageIsTable || enIsTable) {
    const pageLen = pageIsTable ? extractUniDataTableRows(fromPage).length : 0
    const enLen = enIsTable ? extractUniDataTableRows(fromEn).length : 0
    if (pageIsTable && pageLen > 0) return fromPage
    if (pageLen === 0 && enLen > 0) return fromEn
    if (pageIsTable) return fromPage
    if (enIsTable) return fromEn
    return undefined
  }
  if (key in pageFieldsSnapshot) return fromPage
  if (key in enFields) return fromEn
  return undefined
}

export function buildReloadPanelRequestFields(input: {
  pageFieldsSnapshot: Record<string, unknown>
  panelFieldKeys: ReadonlySet<string>
  panelFields: Record<string, unknown>
  enFields: Record<string, unknown>
}): Record<string, unknown> {
  const scopedPageFields: Record<string, unknown> = {}
  for (const key of input.panelFieldKeys) {
    const resolved = resolveReloadPanelScopedPageField(
      key,
      input.pageFieldsSnapshot,
      input.enFields,
    )
    if (resolved !== undefined) {
      scopedPageFields[key] = resolved
    }
  }

  const merged = mergePageFieldMaps(
    { shouldPreserveEmptyIncoming: () => true },
    input.pageFieldsSnapshot,
    input.panelFields,
    input.enFields,
  ) as Record<string, unknown>
  const out = overlayAuthoritativeFieldKeys(merged, scopedPageFields) as Record<string, unknown>

  for (const key of DOCUMENT_IDENTITY_KEYS) {
    if (key in input.pageFieldsSnapshot) {
      out[key] = input.pageFieldsSnapshot[key]
    }
  }

  for (const [key, value] of Object.entries(input.pageFieldsSnapshot)) {
    if (key.startsWith('$')) {
      out[key] = value
    }
  }

  const stripped = stripServerEventOverlayFields(out) as Record<string, unknown>
  return sanitizeClientDataFieldMap(stripped) as Record<string, unknown>
}
